import React, { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { BookOpen, Code, CheckCircle, AlertTriangle, Info, ListOrdered, Compass } from 'lucide-react';

interface TocItem {
  id: string;
  label: string;
  indent?: boolean;
}

const TOC: TocItem[] = [
  { id: 'over-deze-handleiding', label: 'Over deze handleiding' },
  { id: 'widget', label: 'Blog-widget' },
  { id: 'widget-overzicht', label: 'Wat is de blog-widget?', indent: true },
  { id: 'widget-activeren', label: 'Widget activeren', indent: true },
  { id: 'widget-installeren', label: 'Widget installeren', indent: true },
  { id: 'widget-eigen-domein', label: 'Eigen domein instellen', indent: true },
  { id: 'widget-storing', label: 'Widget doet het niet', indent: true },
  { id: 'klanttraject', label: 'Klanttraject' },
  { id: 'klanttraject-overzicht', label: 'Wat is het klanttraject?', indent: true },
  { id: 'klanttraject-huisstijl', label: 'Huisstijl inrichten', indent: true },
];

function SectionCard({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="bg-white rounded-2xl border border-gray-200 p-6 scroll-mt-6">
      <h3 className="text-lg font-bold text-[#231F20] mb-4">{title}</h3>
      {children}
    </section>
  );
}

interface RunbookCardProps {
  id: string;
  title: string;
  wanneer: ReactNode;
  stappen: ReactNode[];
  controleren: ReactNode[];
  fouten?: ReactNode[];
}

function RunbookCard({ id, title, wanneer, stappen, controleren, fouten }: RunbookCardProps) {
  return (
    <section id={id} className="bg-white rounded-2xl border border-gray-200 p-6 scroll-mt-6">
      <h3 className="text-lg font-bold text-[#231F20] mb-4">{title}</h3>

      <div className="space-y-5">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Wanneer gebruik je dit?
          </h4>
          <p className="text-sm text-blue-800">{wanneer}</p>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <ListOrdered className="w-4 h-4" />
            Stappen
          </h4>
          <ol className="space-y-2">
            {stappen.map((stap, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-6 h-6 bg-gray-100 text-gray-700 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                  {i + 1}
                </span>
                <span className="pt-1">{stap}</span>
              </li>
            ))}
          </ol>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Controleren of het gelukt is
          </h4>
          <ul className="space-y-1.5">
            {controleren.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {fouten && fouten.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Veelgemaakte fouten
            </h4>
            <ul className="space-y-1.5">
              {fouten.map((item, i) => (
                <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                  <span className="text-amber-400 mt-0.5">&bull;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

export function AdminHandleiding() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: 'start' });
    };
    scroll();
    const timeout = setTimeout(scroll, 150);
    return () => clearTimeout(timeout);
  }, [location.hash]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start gap-4 mb-8">
          <div className="p-3 bg-blue-100 rounded-lg">
            <BookOpen className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#231F20]">Handleiding</h1>
            <p className="text-sm text-gray-600 mt-1">
              Stap-voor-stap uitleg per onderdeel, geschreven voor iedereen die klanten helpt.
            </p>
          </div>
        </div>

        <div className="flex gap-8">
          <nav className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-6 bg-white rounded-2xl border border-gray-200 p-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Inhoud
              </h2>
              <ul className="space-y-1">
                {TOC.map(item => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollTo(item.id)}
                      className={`w-full text-left text-sm py-1 px-2 rounded hover:bg-gray-100 text-gray-700 hover:text-[#231F20] transition-colors ${
                        item.indent ? 'pl-5 text-gray-500' : 'font-medium'
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          <div className="flex-1 min-w-0 space-y-6">
            <SectionCard id="over-deze-handleiding" title="Over deze handleiding">
              <div className="text-sm text-gray-700 space-y-3">
                <p>
                  Hier staat per onderdeel van dibbi uitgelegd hoe je veelvoorkomende handelingen
                  uitvoert. Elke handeling volgt dezelfde opbouw: wanneer gebruik je dit, de stappen,
                  hoe je controleert of het gelukt is, en de fouten die in de praktijk het vaakst
                  worden gemaakt.
                </p>
                <p>
                  Op verschillende plekken in het dashboard staat een vraagteken-link die direct naar
                  het juiste hoofdstuk hier verwijst. Mis je een handeling of klopt iets niet meer met
                  wat je op je scherm ziet? Geef het door, dan wordt de handleiding bijgewerkt.
                </p>
              </div>
            </SectionCard>

            <div id="widget" className="flex items-center gap-3 pt-4 scroll-mt-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Code className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20]">Blog-widget</h2>
            </div>

            <SectionCard id="widget-overzicht" title="Wat is de blog-widget?">
              <div className="text-sm text-gray-700 space-y-3">
                <p>
                  De blog-widget is een klein stukje code dat de klant op zijn eigen website plaatst.
                  De widget haalt automatisch de gepubliceerde blogartikelen van de klant op en toont
                  ze op die website, inclusief de accentkleur en het logo uit het blogprofiel van de
                  klant.
                </p>
                <p>
                  De widget werkt met een persoonlijke API-sleutel per klant. Die sleutel en alle
                  instellingen vind je op de klantpagina (Gebruikers &rarr; klik op de klant) in de
                  sectie <strong>Widget Configuratie</strong>.
                </p>
                <p>Er zijn twee varianten:</p>
                <ul className="list-disc list-inside space-y-1 ml-1">
                  <li>
                    <strong>Volledige widget</strong>: voor de blogpagina van de klant. Bezoekers zien
                    het overzicht en lezen artikelen op diezelfde pagina.
                  </li>
                  <li>
                    <strong>Compacte widget</strong>: een rij kaartjes voor bijvoorbeeld de homepage
                    of een zijbalk. Klikt een bezoeker op een kaartje, dan gaat hij door naar de
                    hoofdblogpagina.
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-blue-800">
                    <strong>Belangrijk om te weten:</strong> de widget staat los van de blog-links die
                    dibbi gebruikt in nieuwsbrieven en gedeelde berichten. Die links blijven naar
                    lees-nu.nl wijzen totdat het eigen domein van de klant is ingevuld <em>en</em> de
                    schakelaar "Gebruik Custom Domain" aanstaat. De widget aanzetten verandert daar
                    niets aan.
                  </p>
                </div>
              </div>
            </SectionCard>

            <RunbookCard
              id="widget-activeren"
              title="Widget activeren voor een klant"
              wanneer="Een klant wil de widget gaan gebruiken, of de widget op de site van de klant meldt dat hij is uitgeschakeld."
              stappen={[
                <>Ga naar <strong>Gebruikers</strong> en open de klant.</>,
                <>Scroll naar de sectie <strong>Widget Configuratie</strong>.</>,
                <>Zet de schakelaar onder <strong>Widget Status</strong> aan.</>,
              ]}
              controleren={[
                <>De status toont "Widget Ingeschakeld" en het rode label "Gedeactiveerd" bovenin de sectie is verdwenen.</>,
                <>Heeft de klant al een gepubliceerd artikel, open dan de <strong>Test Widget URL</strong> (oranje blok) in een incognitovenster. Het artikel moet laden.</>,
                <>Zodra de widget op de site van de klant draait, wordt "Laatst gebruikt" onder de API-sleutel automatisch bijgewerkt.</>,
              ]}
              fouten={[
                <>Verwachten dat blog-links in nieuwsbrieven nu naar de site van de klant wijzen. Activeren zet alleen de gegevenstoegang aan; links veranderen pas na het instellen van een eigen domein (zie "Eigen domein instellen").</>,
                <>De widget op de site doet het daarna nog steeds niet. Controleer dan of de API-sleutel in de code op de site exact gelijk is aan de sleutel op de klantpagina (klik op het oogje om de sleutel te tonen).</>,
              ]}
            />

            <RunbookCard
              id="widget-installeren"
              title="Widget installeren op de site van de klant"
              wanneer="De klant heeft de widget nog niet op zijn website staan en vraagt hoe dat moet."
              stappen={[
                <>Open de klant en ga naar <strong>Widget Configuratie</strong>. Controleer dat de widget aanstaat (zie "Widget activeren").</>,
                <>Kies het widgettype: <strong>Volledige widget</strong> voor de blogpagina, <strong>Compacte widget</strong> voor bijvoorbeeld de homepage of een zijbalk.</>,
                <>Kies het aantal artikelen dat de widget moet tonen.</>,
                <>Klik op <strong>Kopieer Code</strong> en stuur de embedcode naar de klant, of plak hem zelf in de site-editor van de klant.</>,
                <>De code hoort op de plek in de pagina waar de blogartikelen moeten verschijnen.</>,
              ]}
              controleren={[
                <>Open de pagina van de klant: de artikelen verschijnen vanzelf.</>,
                <>Volledige widget: klik een artikel aan. Het opent op dezelfde pagina en "Terug naar overzicht" werkt.</>,
                <>Compacte widget: klik een kaartje aan. Je komt uit op de hoofdblogpagina van de klant.</>,
              ]}
              fouten={[
                <>De compacte widget verwijst door naar de hoofdblogpagina. Die werkt dus alleen goed als de volledige widget ook ergens op de site staat.</>,
                <>De widget blijft leeg zonder foutmelding: controleer of de klant al artikelen heeft gepubliceerd.</>,
                <>De embedcode is in de site-editor van de klant geplakt als gewone tekst in plaats van als HTML-blok. De code moet als HTML/embed-element worden toegevoegd.</>,
              ]}
            />

            <RunbookCard
              id="widget-eigen-domein"
              title="Eigen domein instellen"
              wanneer="De klant heeft de widget op zijn site staan en wil dat blog-links (bijvoorbeeld in nieuwsbrieven) naar zijn eigen website wijzen in plaats van naar lees-nu.nl."
              stappen={[
                <>Controleer eerst dat de volledige widget werkt op de site van de klant (zie "Widget installeren"). Dit is de belangrijkste stap.</>,
                <>Vul bij <strong>Custom Domain URL</strong> het volledige adres in van de pagina waar de widget staat, inclusief https://. Bijvoorbeeld: https://janbakker.nl/blog</>,
                <>Controleer het blauwe blok <strong>URL Preview</strong>: zo gaan de links eruitzien.</>,
                <>Open de <strong>Test Widget URL</strong> in een incognitovenster en controleer dat het artikel echt opent op de site van de klant.</>,
                <>Zet pas daarna de schakelaar <strong>Gebruik Custom Domain</strong> aan en klik op <strong>Wijzigingen Opslaan</strong>.</>,
              ]}
              controleren={[
                <>Het groene blok "Lees-Nu URL voor deze gebruiker" toont nu het eigen domein van de klant.</>,
                <>Nieuwe nieuwsbrieven en gedeelde links gebruiken vanaf nu het eigen domein van de klant.</>,
              ]}
              fouten={[
                <>De schakelaar aanzetten voordat de widget op de site staat. Links in nieuwsbrieven komen dan uit op een pagina zonder blog: een doodlopende link voor lezers van de klant.</>,
                <>De homepage invullen terwijl de widget op een aparte blogpagina staat. Artikelen openen dan niet. Vul altijd de exacte pagina-URL in waar de widget staat.</>,
                <>De URL zonder https:// invullen.</>,
              ]}
            />

            <RunbookCard
              id="widget-storing"
              title="Widget doet het niet"
              wanneer="Een klant meldt dat de blog op zijn website leeg blijft, een foutmelding toont of verouderd is. Loop de stappen in deze volgorde door."
              stappen={[
                <>Open de pagina van de klant waar de widget staat en kijk wat je ziet.</>,
                <><strong>Foutmelding over de API-sleutel</strong> ("Invalid or disabled API key"): controleer op de klantpagina of Widget Status aanstaat. Staat hij aan, vergelijk dan de sleutel in de paginacode van de klant met de sleutel in het dashboard (oogje &rarr; toon sleutel). Verschillen ze, stuur de klant de actuele embedcode.</>,
                <><strong>Widget is helemaal onzichtbaar</strong> (ook geen leeg blok): de embedcode staat waarschijnlijk niet meer op de pagina, of de site-editor heeft het script verwijderd. Installeer opnieuw (zie "Widget installeren").</>,
                <><strong>Widget laadt maar toont geen artikelen</strong>: controleer of de klant gepubliceerde artikelen heeft.</>,
                <><strong>Doorklikken vanaf de compacte widget werkt niet</strong>: controleer of de volledige widget op de hoofdblogpagina van de klant staat.</>,
              ]}
              controleren={[
                <>Ververs na de fix de pagina van de klant in een incognitovenster: de artikelen verschijnen weer.</>,
                <>"Laatst gebruikt" op de klantpagina wordt bijgewerkt zodra de widget weer gegevens ophaalt.</>,
              ]}
              fouten={[
                <>Oudere klanten kunnen een API-sleutel in een ouder formaat hebben (zonder "wapi_" ervoor). Dat is geen probleem. Het gaat er alleen om dat de sleutel op de site exact gelijk is aan die in het dashboard.</>,
              ]}
            />

            <div id="klanttraject" className="flex items-center gap-3 pt-4 scroll-mt-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Compass className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-[#231F20]">Klanttraject</h2>
            </div>

            <SectionCard id="klanttraject-overzicht" title="Wat is het klanttraject?">
              <div className="text-sm text-gray-700 space-y-3">
                <p>
                  Het klanttraject is de geautomatiseerde reis die elke nieuwe klant doorloopt: een
                  vaste keten van mails, vriendelijke duwtjes en felicitatiemomenten, van
                  accountaanmaak tot de eerste nieuwsbrief. De meeste stappen lopen vanzelf.
                </p>
                <p>Je vindt het traject op twee plekken:</p>
                <ul className="list-disc list-inside space-y-1 ml-1">
                  <li>
                    <strong>Klanttraject</strong> in het menu: het overzicht van alle klanten, met
                    bovenaan de openstaande taken die op jou wachten.
                  </li>
                  <li>
                    De <strong>tijdlijn per klant</strong>: open een klant en ga naar het tabblad
                    Klanttraject. Daar zie je elke stap, wat er al is gebeurd en wat er nog komt. Het
                    vraagteken bij een stap legt uit wat die stap doet.
                  </li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-blue-800">
                    <strong>Belangrijk om te weten:</strong> sommige stappen zijn interne taken die op
                    support wachten ("Wacht op support"). De reis staat dan stil tot jij de taak
                    afrondt en op <strong>Markeer OK</strong> klikt. Daarna loopt de keten automatisch
                    verder, vaak met een mail aan de klant als eerstvolgende stap.
                  </p>
                </div>
              </div>
            </SectionCard>

            <RunbookCard
              id="klanttraject-huisstijl"
              title="Huisstijl inrichten voor een nieuwe klant"
              wanneer={<>Er staat een taak "Huisstijl inrichten" met "Wacht op support" in de openstaande taken of op de tijdlijn van een klant. Jij richt dan handmatig het logo en de huisstijlkleur van de klant in; daarna gaat automatisch de "huisstijl is klaar"-mail naar de klant.</>}
              stappen={[
                <>Open de klant en kijk op het tabblad <strong>Overzicht</strong> welke website de klant heeft. Daar haal je het logo en de kleuren vandaan.</>,
                <>Ga naar het tabblad <strong>Blog</strong> en klik op <strong>Bewerken</strong>.</>,
                <>Upload het logo van de klant (PNG, JPG, SVG of WEBP, maximaal 2MB). Vaak kun je het logo van de website van de klant opslaan; vraag anders de klant om het logo te mailen.</>,
                <>Stel de <strong>Accentkleur</strong> in op de belangrijkste kleur uit de huisstijl van de klant. Deze kleur wordt gebruikt voor links, quotes en knoppen op de blogpagina en in mails.</>,
                <>Loop de <strong>Over Ons-tekst</strong> na en sla de wijzigingen op.</>,
                <>Ga naar het tabblad <strong>Klanttraject</strong> en klik bij "Huisstijl inrichten" op <strong>Markeer OK</strong>.</>,
              ]}
              controleren={[
                <>De stap staat op voltooid en de volgende stap "Huisstijl-klaar mail" komt in de wachtrij. De klant krijgt die mail automatisch; ongeveer een uur later volgt de welkomstmail.</>,
                <>Open de blogpagina van de klant (de groene "Lees-Nu URL" op het tabblad Widget) en controleer dat het logo en de kleur kloppen.</>,
              ]}
              fouten={[
                <>Op Markeer OK klikken voordat de huisstijl echt is ingericht. De klant krijgt dan direct de mail "je huisstijl is klaar" terwijl er nog niets staat.</>,
                <>De taak laten liggen. De reis staat stil zolang de taak open staat, dus de klant krijgt ook geen vervolgmails. Na ongeveer twee werkdagen krijg je een herinneringsmail, en die blijft elke paar dagen komen totdat de taak is afgerond.</>,
                <>"Sla over" gebruiken om van de taak af te zijn. Ook dan loopt de reis door en krijgt de klant alsnog de huisstijl-mail, zonder dat er iets is ingericht.</>,
              ]}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
